/**
 * Judging a spoken answer against the card.
 *
 * Typing an answer is exact; saying one is not. Speech recognition drops
 * accents, guesses homophones, mishears short words and sometimes wraps the
 * answer in filler ("um, I think it's the friend"). A matcher strict enough to
 * be correct on paper would make hands-free study miserable, and one loose
 * enough to never complain would teach nothing. Everything here is the
 * negotiation between those two.
 *
 * It is a pure function on purpose. This is where the bugs live, and a bug here
 * is a learner being told they were wrong when they were right — which is worse
 * than a crash, because it is quiet. Every rule below has a test.
 *
 * The rules come from the content as it actually is, not from guesses:
 *
 * - 7.6% of translations carry a parenthetical gloss — "the artist (male,
 *   female)", "to know (a fact)". Nobody says it out loud, so it is dropped.
 * - 11.2% list synonyms with commas — "the little boy, the child". Any one of
 *   them is the answer. Parentheses are removed *first*, so the comma inside
 *   "(male, female)" never splits anything.
 * - 15.7% of translations are infinitives written "to sing". Saying "sing" is
 *   not an error worth flagging, and it is the same class of slip as dropping
 *   an article.
 * - A handful use slashes, and they mean two different things: " / " with
 *   spaces separates whole phrases, while "his/her/their" alternates a single
 *   word in place.
 */

export type MatchVerdict = 'match' | 'close' | 'miss';

export interface SpokenMatch {
  verdict: MatchVerdict;
  /** The acceptable form the answer came nearest to, in its original spelling. */
  closestTo: string;
  /** What was actually compared, for when the UI has to explain itself. */
  normalizedHeard: string;
}

export interface MatchOptions {
  /**
   * Whether a missing or wrong article demotes the answer.
   *
   * Speaking the target language, "el" versus "la" *is* the lesson, so a slip
   * there is worth surfacing. Speaking the translation it is noise — nobody is
   * learning English gender from this app.
   */
  articlesMatter: boolean;
}

/**
 * Words that may be dropped from the front of an answer without it being wrong.
 *
 * Keyed by the base language code; anything absent simply has no optional lead,
 * which is the safe default — an unknown language gets exact matching rather
 * than a guess about its grammar.
 */
const OPTIONAL_LEADS: Record<string, string[]> = {
  en: ['the', 'a', 'an', 'to'],
  es: ['el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas'],
};

/** Guards the slash expansion below from a pathological card. */
const MAX_VARIANTS = 24;

/** Combining marks, left behind by NFD once the base letter is separated. */
const COMBINING_MARKS = /[̀-ͯ]/g;

/** Punctuation and the typographic quotes recognition engines like to emit. */
const PUNCTUATION = /[!?.,;:"'`¡¿´‘’“”()[\]{}…—–-]/g;

/**
 * Lowercase, unaccented, unpunctuated, single-spaced.
 *
 * Stripping accents also folds ñ to n, which collapses "año" and "ano". That is
 * a real loss and it is taken deliberately: recognition engines are not
 * dependable about either accents or ñ, so keeping them would fail honest
 * answers far more often than it would catch dishonest ones.
 */
export function normalizeSpoken(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(PUNCTUATION, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Expands "his/her/their car" into one phrase per alternative. */
function expandInlineSlashes(phrase: string): string[] {
  const words = phrase.trim().split(/\s+/).filter(Boolean);
  let variants: string[][] = [[]];

  for (const word of words) {
    const choices = word.includes('/') ? word.split('/').filter(Boolean) : [word];
    const next: string[][] = [];
    for (const base of variants) {
      for (const choice of choices) {
        if (next.length >= MAX_VARIANTS) break;
        next.push([...base, choice]);
      }
    }
    variants = next;
  }

  return variants.map((w) => w.join(' '));
}

/**
 * Every form of `expected` that should be accepted, in original spelling.
 *
 * Exported because the drill shows them when an answer misses — being told
 * "close" without being told what the answer was is the least useful thing a
 * flashcard can do.
 */
export function acceptableAnswers(expected: string): string[] {
  // Parentheses first, so the comma in "(male, female)" cannot split a phrase.
  const withoutGloss = expected.replace(/\([^)]*\)/g, ' ');

  const out: string[] = [];
  for (const bySense of withoutGloss.split(/[;,]/)) {
    // " / " with spaces separates whole phrases; a bare slash alternates a word.
    for (const phrase of bySense.split(/\s+\/\s+/)) {
      for (const variant of expandInlineSlashes(phrase)) {
        const trimmed = variant.replace(/\s+/g, ' ').trim();
        if (trimmed) out.push(trimmed);
      }
    }
  }

  // An expected string that was *entirely* a gloss leaves nothing to match, so
  // fall back to the original rather than accepting anything at all.
  const unique = [...new Set(out)];
  return unique.length > 0 ? unique : [expected.trim()];
}

/** Drops one leading article or infinitive marker, if there is one. */
function withoutLead(normalized: string, languageCode: string): string {
  const leads = OPTIONAL_LEADS[languageCode.toLowerCase().split('-')[0]] ?? [];
  for (const lead of leads) {
    if (normalized.startsWith(`${lead} `)) return normalized.slice(lead.length + 1);
  }
  return normalized;
}

/** Standard Levenshtein, two rows rather than a full matrix. */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }

  return prev[b.length];
}

/**
 * How much misrecognition to forgive, scaled to the length of the answer.
 *
 * One character on a short word is the difference between two real words, so
 * the allowance starts at one and grows slowly. A long sentence mangled by a
 * single dropped syllable should still pass.
 */
function tolerance(length: number): number {
  return 1 + Math.floor(length / 8);
}

/** True when `needle` appears in `haystack` on whole-word boundaries. */
function containsPhrase(haystack: string, needle: string): boolean {
  // Only for answers substantial enough that an accidental hit is unlikely.
  // "a" appearing inside a rambling sentence means nothing.
  if (needle.length < 4 && !needle.includes(' ')) return false;
  return ` ${haystack} `.includes(` ${needle} `);
}

/**
 * Grades what was heard against what the card expects.
 *
 * `close` means recognised but imperfect — the right word with the wrong
 * article, or a phrase the engine mangled. The drill counts it as correct and
 * speaks the exact form back, because the learner plainly knew the word and the
 * review schedule will bring it round again anyway. Refusing it would mostly
 * punish the microphone.
 */
export function matchSpokenAnswer(
  heard: string,
  expected: string,
  languageCode: string,
  options: MatchOptions,
): SpokenMatch {
  const normalizedHeard = normalizeSpoken(heard);
  const forms = acceptableAnswers(expected);

  let best: SpokenMatch = {
    verdict: 'miss',
    closestTo: forms[0] ?? expected,
    normalizedHeard,
  };

  if (!normalizedHeard) return best;

  // 'match' beats 'close' beats 'miss'; an exact hit ends the search outright.
  const rank: Record<MatchVerdict, number> = { match: 2, close: 1, miss: 0 };
  const consider = (verdict: MatchVerdict, closestTo: string) => {
    if (rank[verdict] > rank[best.verdict]) best = { verdict, closestTo, normalizedHeard };
  };

  for (const form of forms) {
    const normalizedForm = normalizeSpoken(form);
    if (!normalizedForm) continue;

    if (normalizedHeard === normalizedForm) {
      return { verdict: 'match', closestTo: form, normalizedHeard };
    }

    // Differing only by a leading article or "to".
    const heardBare = withoutLead(normalizedHeard, languageCode);
    const formBare = withoutLead(normalizedForm, languageCode);
    if (heardBare === formBare) {
      consider(options.articlesMatter ? 'close' : 'match', form);
      continue;
    }

    if (containsPhrase(normalizedHeard, normalizedForm)) {
      consider('close', form);
      continue;
    }

    if (editDistance(normalizedHeard, normalizedForm) <= tolerance(normalizedForm.length)) {
      consider('close', form);
      continue;
    }

    // A mangled article should not hide an otherwise good answer.
    if (editDistance(heardBare, formBare) <= tolerance(formBare.length)) {
      consider('close', form);
    }
  }

  return best;
}
